'use client';

import { type ReactNode, useState } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { BasicModal } from '@/app_components/common/BasicModal';
import { Icon } from '@/app_components/common/Icon';
import { Link } from '@/app_components/common/Link';
import { Logo } from '@/app_components/common/Logo';
import type { NestedMenuItem } from '@/app_lib/types/NestedMenuItem';

const Content = ({ content, children }) => {
  const [activeSubMenu, setActiveSubMenu] = useState<NestedMenuItem | null>(null);
  return (
    <div className='bg-pure h-screen w-screen md:w-96 flex flex-col pb-6'>
      <div className='flex items-center gap-6 px-4 border-b border-neutral-300 py-4'>
        <Logo className='w-24 ml-14 md:ml-11 items-center justify-center flex' />
        <ActionableIcon className='ml-auto'>
          {/** biome-ignore lint/nursery/noRestrictedElements: TODO: This currently needs to be a label due to how Queryly functions, but we should look into doing this differently */}
          <label
            className='hover:cursor-pointer text-primary-hover'
            htmlFor='queryly_toggle'
          >
            <Icon icon='Search' />
          </label>
        </ActionableIcon>
      </div>
      <div className='overflow-y-scroll scrollbar-hidden'>
        {activeSubMenu === null ? (
          <ul className='flex flex-col gap-1 md:gap-6 bg-pure pl-9 pr-5 py-6 font-semibold text-xl slide-from-left 0.1s'>
            {content.map((item: NestedMenuItem) => (
              <li
                className='flex items-center justify-between min-h-[44px] md:min-h-auto'
                key={item.id}
              >
                <Link
                  className='hover:text-neutral-600'
                  href={item.url}
                >
                  {item.label}
                </Link>
                {item.subMenu.length > 0 && (
                  <ActionableIcon onClick={() => setActiveSubMenu(item)}>
                    <Icon icon='ChevronRight' />
                  </ActionableIcon>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <ul className='flex flex-col gap-1 md:gap-6 bg-pure pl-9 pr-5 py-6 font-semibold text-xl slide-from-right'>
            <li
              className='flex items-center md:gap-2'
              key={activeSubMenu.id}
            >
              <ActionableIcon
                className='-ml-2 md:-ml-4 pr-8 md:pr-4'
                onClick={() => setActiveSubMenu(null)}
              >
                <Icon icon='ChevronLeft' />
              </ActionableIcon>
              <Link
                className='hover:text-neutral-600'
                href={activeSubMenu.url}
              >
                {activeSubMenu.label}
              </Link>
            </li>
            {activeSubMenu.subMenu.map((subItem) => (
              <li
                className='flex items-center pl-12 md:pl-8 min-h-[44px] md:min-h-auto'
                key={subItem.id}
              >
                <Link
                  className='hover:text-neutral-600'
                  href={subItem.url}
                >
                  {subItem.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
        <div className='absolute md:hidden inset-x-0 bottom-0 px-4 bg-pure py-2 border-t stroke-secondary'>
          {children}
        </div>
      </div>
    </div>
  );
};

export const DrawerMenu = ({ content, children }: { content: NestedMenuItem[]; children: ReactNode }) => {
  return (
    <BasicModal
      closeClassName='top-4.5 left-5'
      content={<Content content={content}>{children}</Content>}
      description='Navigate through Fortune sections and subsections'
      position='left'
      title='DrawerMenu'
      trigger={
        <ActionableIcon>
          <Icon icon='Menu' />
        </ActionableIcon>
      }
    />
  );
};
